import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Check, X } from "lucide-react";
import clsx from "clsx";
import { Api } from "../lib/api.js";

/**
 * Quick post-watch feedback for an item — 3×3 grid of pre-defined reactions
 * that map directly to ranking signals (so they don't need LLM evaluation),
 * plus a free-text textarea for nuance.
 *
 * Per the design transcript: voice input is a TV-side thing; on the web it's
 * grid-or-typed.
 */

interface QuickReaction {
  label: string;
  // Signal vector contribution — values written to item_comments.llm_signals.
  signals: Record<string, number>;
}

const QUICK_REACTIONS: QuickReaction[] = [
  { label: "Clickbait",         signals: { clickbait_complaint: 0.9, title_mismatch: 0.8 } },
  { label: "Title misleading",  signals: { title_mismatch: 0.9 } },
  { label: "Too long",          signals: { pacing_too_slow: 0.6, length_complaint: 0.8 } },
  { label: "Too short",         signals: { length_complaint: -0.8 } },
  { label: "Off topic",         signals: { off_topic_drift: 0.9 } },
  { label: "Great pacing",      signals: { pacing_too_slow: -0.6, production_quality_like: 0.5 } },
  { label: "Loved it",          signals: { production_quality_like: 0.8, creator_like: 0.6 } },
  { label: "More like this",    signals: { creator_like: 0.8, more_like_this: 1.0 } },
  { label: "Less like this",    signals: { creator_dislike: 0.7, more_like_this: -1.0 } },
];

export function CommentComposer({
  profileId,
  itemId,
}: {
  profileId: string;
  itemId: string;
}): JSX.Element {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [sent, setSent] = useState(false);

  const submit = useMutation({
    mutationFn: async () => {
      // Quick reactions go in as the comment text + pre-baked signals.
      // Free-text comments go through the LLM-eval worker (server-side).
      const reactionLabels = [...selected].map((i) => QUICK_REACTIONS[i]!.label);
      const mergedSignals: Record<string, number> = {};
      for (const idx of selected) {
        for (const [k, v] of Object.entries(QUICK_REACTIONS[idx]!.signals)) {
          mergedSignals[k] = (mergedSignals[k] ?? 0) + v;
        }
      }

      const composedText = [
        reactionLabels.length > 0 ? `[${reactionLabels.join(", ")}]` : null,
        text.trim() || null,
      ].filter(Boolean).join(" ").trim();

      if (composedText.length === 0) throw new Error("Pick a reaction or write something");

      await Api.library.comment({
        profile_id: profileId,
        item_id: itemId,
        text: composedText,
        llm_signals: Object.keys(mergedSignals).length > 0 ? mergedSignals : undefined,
      });
    },
    onSuccess: () => {
      setSent(true);
      setText("");
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ["recs", profileId] });
      setTimeout(() => setSent(false), 3000);
    },
  });

  return (
    <details className="card mt-4">
      <summary className="px-4 py-2 text-sm cursor-pointer text-parchment hover:text-ivory inline-flex items-center gap-2">
        <MessageSquare size={14} /> Leave feedback
      </summary>
      <div className="p-4 space-y-4">
        <div>
          <div className="section-label mb-2">Quick reactions</div>
          <div className="grid grid-cols-3 gap-2">
            {QUICK_REACTIONS.map((r, i) => {
              const isSelected = selected.has(i);
              return (
                <button
                  key={r.label}
                  onClick={() => {
                    const next = new Set(selected);
                    if (next.has(i)) next.delete(i);
                    else next.add(i);
                    setSelected(next);
                  }}
                  className={clsx(
                    "px-3 py-2 rounded-md text-sm border transition-colors text-left",
                    isSelected
                      ? "bg-vermilion border-goldHi text-ivory"
                      : "border-hairline text-parchment hover:text-ivory hover:bg-elev",
                  )}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <div className="section-label mb-2">Or type something</div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="What did you notice?"
            className="w-full bg-ink/60 border border-hairline rounded-md px-3 py-2 text-ivory focus:border-gold outline-none text-sm"
          />
          <div className="text-parchment text-xs mt-1">
            Free-text comments get evaluated by Claude to extract preference signals.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => submit.mutate()}
            disabled={submit.isPending || (selected.size === 0 && text.trim().length === 0)}
            className="pill"
          >
            {submit.isPending ? "Sending…" : "Send feedback"}
          </button>
          {sent && (
            <span className="text-jade text-sm inline-flex items-center gap-1">
              <Check size={14} /> Logged
            </span>
          )}
          {submit.isError && (
            <span className="text-vermilionHi text-sm inline-flex items-center gap-1">
              <X size={14} /> {(submit.error as Error).message}
            </span>
          )}
        </div>
      </div>
    </details>
  );
}
